import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';
import { Mutex } from 'async-mutex';

const mutex = new Mutex();
const baseUrl = import.meta.env.VITE_API_BASE_URL;
const botSignals = {
  webdriver: navigator.webdriver === true,
  plugins: navigator.plugins?.length || 0,
  languages: navigator.languages?.length || 0,
};

export const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Add user timezone to headers
    if (timezone) {
      headers.set('X-User-Timezone', timezone);
    }
    // Add bot signals to headers
    headers.set('X-Bot-Signals', JSON.stringify(botSignals));
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

export const baseQueryV1 = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          { url: 'auth/refresh-token', method: 'POST' },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          const pathname = window.location.pathname;
          const isPublicAuthRoute =
            pathname === '/' ||
            pathname === '/login-register' ||
            pathname.startsWith('/verify/payslip/');

          if (!isPublicAuthRoute) {
            window.location.href = '/login-register';
          }
        }
      } finally {
        // release must be called once the mutex should be released
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  if (result.error && result.error.status === 429) {
    toast.error(result.error.data?.message || 'Too many requests');
  }

  if (result.error && result.error.status === 500) {
    toast.error(result.error.data?.message || 'Server error');
  }
  if (result.error && result.error.status === 409) {
    toast.error(result.error.data?.message || 'Something went wrong');
  }
  if (result.error && result.error.status === 404) {
    toast.error(result.error.data?.message || 'Details not found');
  }
  if (result.error && result.error.status === 403) {
    toast.error(result.error.data?.message || 'You are not authorized to perform this action');
  }
  if (result.error && result.error.status === 400) {
    toast.error(
      result.error.data?.message || result.error.data?.errors?.[0]?.msg || 'Something went wrong',
    );
  }

  return result;
};
